import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/common";
import { AuthLayout, MfaStep } from "./LoginView";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";

export function MfaSetupPanel({ setupToken, onEnabled }) {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const start = async () => { setBusy(true); try { const { data } = await api.post("/auth/mfa/setup", setupToken ? { setup_token: setupToken } : {}); setSetup(data); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); } };
  const enable = async () => {
    setBusy(true); setErr("");
    try { const { data } = await api.post("/auth/mfa/enable", { code, setup_token: setupToken || undefined }); setCodes(data.backup_codes); onEnabled && onEnabled(data); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  if (codes) return (
    <div className="space-y-3" data-testid="mfa-backup-codes">
      <p className="text-sm">Two-factor authentication is enabled. Store these one-time backup codes safely:</p>
      <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-slate-950 text-slate-100 rounded p-4">{codes.map((c) => <span key={c}>{c}</span>)}</div>
    </div>
  );
  if (!setup) return <div className="space-y-3"><p className="text-sm text-muted-foreground">Scan a QR code with Google Authenticator, 1Password, Authy or any TOTP app.</p>{err && <p className="text-sm text-red-600">{err}</p>}<Button data-testid="mfa-setup-start-btn" onClick={start} disabled={busy}>Start setup</Button></div>;
  return (
    <div className="space-y-4" data-testid="mfa-setup-panel">
      <div className="flex flex-col sm:flex-row gap-4 items-start">
        <img src={`data:image/png;base64,${setup.qr_png_base64}`} alt="TOTP QR" className="h-40 w-40 border rounded bg-white" data-testid="mfa-qr" />
        <div className="text-xs space-y-2"><div className="eyebrow">Manual key</div><code className="block bg-secondary p-2 rounded break-all" data-testid="mfa-secret">{setup.secret}</code><p className="text-muted-foreground">Enter the 6-digit code shown by your app to confirm.</p></div>
      </div>
      <Field label="Verification code"><Input data-testid="mfa-setup-code" value={code} onChange={(e) => setCode(e.target.value)} className="bg-card font-mono" maxLength={6} /></Field>
      {err && <p className="text-sm text-red-600" data-testid="mfa-setup-error">{err}</p>}
      <Button data-testid="mfa-setup-enable-btn" onClick={enable} disabled={busy || code.length !== 6}>Enable 2FA</Button>
    </div>
  );
}

export default function PlatformLoginView() {
  const { t } = useT();
  const { loginWithTokens } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState({ kind: "login" });
  const finish = async (data) => { await loginWithTokens({ access_token: data.access_token, refresh_token: data.refresh_token, scope: "platform" }); navigate("/platform-admin"); };
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const { data } = await api.post("/platform/auth/login", form);
      if (data.mfa_setup_required) setStep({ kind: "setup", token: data.setup_token });
      else if (data.mfa_required) setStep({ kind: "mfa", token: data.mfa_token });
      else await finish(data);
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  const [pending, setPending] = useState(null);
  return (
    <AuthLayout platform title={step.kind === "login" ? t("auth.platform_login") : step.kind === "setup" ? "Secure your account" : t("auth.mfa.title")} subtitle={step.kind === "setup" ? "Platform administrators must enable two-factor authentication before the first session." : step.kind === "login" ? "Restricted area. All actions are audited." : null}>
      {step.kind === "login" && (
        <form onSubmit={submit} className="space-y-5" data-testid="platform-login-form">
          <Field label={t("auth.email")}><Input data-testid="platform-login-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-card" /></Field>
          <Field label={t("auth.password")}><Input data-testid="platform-login-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-card" /></Field>
          {err && <p className="text-sm text-red-600" data-testid="platform-login-error">{err}</p>}
          <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700" data-testid="platform-login-submit-btn" disabled={busy}>{t("auth.login")}</Button>
          <p className="text-sm text-muted-foreground"><Link to="/login" className="text-blue-600" data-testid="go-tenant-login-link">← Customer workspace login</Link></p>
        </form>
      )}
      {step.kind === "setup" && (<div className="space-y-4"><MfaSetupPanel setupToken={step.token} onEnabled={(d) => setPending(d)} />{pending && <Button className="w-full bg-teal-600 hover:bg-teal-700" data-testid="platform-continue-btn" onClick={() => finish(pending)}>Continue to platform</Button>}</div>)}
      {step.kind === "mfa" && <MfaStep mfaToken={step.token} onDone={finish} />}
    </AuthLayout>
  );
}
